import { useEffect, useId, useRef, useState } from 'react'
import type { KeyboardEvent } from 'react'
import { searchSrsWords } from '@/api/admin'
import { useDismiss } from '@/hooks/useDismiss'
import { Spinner } from '@/components/ui/Spinner'
import { spanLabel } from './labGeometry'
import type { LabWord } from '@/types/admin'

/**
 * Finds one of the admin's own tracked words to simulate. A combobox rather than
 * a plain input so the arrow keys walk the suggestions; typing Space never
 * selects, since lemmas are typed with spaces.
 */
export function LabWordSearch({ onPick }: { onPick: (word: LabWord) => void }) {
  const id = useId()
  const wrapRef = useRef<HTMLDivElement>(null)
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<LabWord[] | null>(null)
  const [error, setError] = useState(false)
  const [open, setOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)

  useDismiss(wrapRef, () => setOpen(false), open)

  useEffect(() => {
    if (!open) return
    setResults(null)
    const timer = setTimeout(() => {
      searchSrsWords(query)
        .then((res) => {
          setResults(res.data.data)
          setError(false)
          setActiveIndex(0)
        })
        .catch(() => setError(true))
    }, 180)
    return () => clearTimeout(timer)
  }, [query, open])

  function pick(word: LabWord) {
    onPick(word)
    setQuery(word.lemma)
    setOpen(false)
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    const count = results?.length ?? 0
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      setOpen(true)
      if (count) setActiveIndex((i) => (i + (event.key === 'ArrowDown' ? 1 : count - 1)) % count)
    } else if (event.key === 'Enter' && open && results?.[activeIndex]) {
      event.preventDefault()
      pick(results[activeIndex])
    } else if (event.key === 'Escape') {
      setOpen(false)
    }
  }

  const listId = `${id}-list`
  const optionId = (i: number) => `${id}-opt-${i}`

  return (
    <div ref={wrapRef} className="relative">
      <label htmlFor={`${id}-input`} className="font-mono text-xs uppercase tracking-widest text-muted">
        Find one of your words
      </label>
      <input
        id={`${id}-input`}
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={open && results?.length ? optionId(activeIndex) : undefined}
        autoComplete="off"
        spellCheck={false}
        placeholder="Schule, casa, hus…"
        value={query}
        onChange={(event) => {
          setQuery(event.target.value)
          setOpen(true)
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKeyDown}
        className="mt-2 w-full min-h-11 rounded-md border border-border-divider bg-surface-bg px-4 py-3 font-serif text-base text-body placeholder:text-muted/60 transition-colors duration-200 focus-visible:border-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/30"
      />
      {open && (
        <div className="absolute inset-x-0 top-full z-20 mt-2 overflow-hidden rounded-md border border-border-divider bg-card shadow-lg">
          {error ? (
            <p className="px-4 py-3 font-serif text-sm text-muted">Could not search your words. Try again.</p>
          ) : results === null ? (
            <p className="flex items-center gap-2 px-4 py-3 font-serif text-sm text-muted">
              <Spinner />
              Searching…
            </p>
          ) : results.length === 0 ? (
            <p className="px-4 py-3 font-serif text-sm text-muted">
              No tracked word matches “{query}”.
            </p>
          ) : (
            <ul id={listId} role="listbox" aria-label="Your words" className="max-h-72 overflow-y-auto py-1">
              {results.map((word, i) => (
                <li
                  key={word.id}
                  id={optionId(i)}
                  role="option"
                  aria-selected={i === activeIndex}
                  onMouseEnter={() => setActiveIndex(i)}
                  onMouseDown={(event) => {
                    event.preventDefault()
                    pick(word)
                  }}
                  className={`flex min-h-11 cursor-pointer items-baseline justify-between gap-4 px-4 py-2.5 transition-colors duration-200 ${
                    i === activeIndex ? 'bg-btn-quiet-bg' : ''
                  }`}
                >
                  <span className="min-w-0 truncate">
                    <span className="font-serif text-base text-heading">{word.lemma}</span>
                    <span className="ml-2 font-serif text-sm text-muted">{word.gloss}</span>
                  </span>
                  <span className="shrink-0 font-mono text-xs uppercase tracking-widest text-muted">
                    {word.language_name} · {spanLabel(word.interval_days)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}
